/**
 * Smart Form Field Component
 *
 * Provides enhanced form field behavior optimized for mobile devices.
 * Keyboard positioning is handled entirely by CSS scroll-margin + useKeyboardInset (System A).
 * This component provides a thin wrapper and a minimal JS safety-net scrollIntoView on focus.
 */

import React, { useRef } from 'react'

interface SmartFormFieldProps {
  children: React.ReactNode
  autoScroll?: boolean
  scrollBehavior?: 'smooth' | 'auto'
  className?: string
  style?: React.CSSProperties
}

export function SmartFormField({
  children,
  autoScroll = true,
  scrollBehavior = 'smooth',
  className = '',
  style = {}
}: SmartFormFieldProps) {
  return (
    <div className={`relative ${className}`} style={style}>
      {children}
    </div>
  )
}

/**
 * Enhanced Input Component with Mobile Optimizations
 */
interface MobileOptimizedInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string
  helperText?: string
  onEnterPress?: () => void
  preventZoom?: boolean
}

export function MobileOptimizedInput({
  label,
  helperText,
  onEnterPress,
  preventZoom = true,
  onKeyDown,
  className = '',
  style = {},
  ...props
}: MobileOptimizedInputProps) {
  const inputRef = useRef<HTMLInputElement>(null)

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && onEnterPress && !props.disabled) {
      e.preventDefault()
      onEnterPress()
    }

    if (onKeyDown) {
      onKeyDown(e)
    }
  }

  const parsePxVar = (name: string, fallback: number): number => {
    const raw = getComputedStyle(document.documentElement).getPropertyValue(name)
    const parsed = Number.parseFloat(raw)
    return Number.isFinite(parsed) ? parsed : fallback
  }

  // Safety-net scroll: only nudge when keyboard/focus actually obscures input.
  const handleFocus = () => {
    setTimeout(() => {
      const input = inputRef.current
      if (!input) return

      const rect = input.getBoundingClientRect()
      const viewportHeight = window.visualViewport?.height ?? window.innerHeight

      const topSafe = 88 // header + progress + breathing room
      const navHeight = parsePxVar('--nav-height', 100)
      const bottomSafe = navHeight + 24

      const isAboveViewport = rect.top < topSafe
      const isBelowViewport = rect.bottom > (viewportHeight - bottomSafe)

      if (isAboveViewport || isBelowViewport) {
        input.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'nearest'
        })
      }
    }, 150)
  }

  const inputClassName = `w-full bg-white rounded-xl border-2 border-gray-200 font-semibold text-center text-gray-800 placeholder-gray-400 focus:outline-none focus:border-brand-blue focus:ring-4 focus:ring-blue-100 transition-all duration-200 shadow-sm ${className}`

  const inputStyle = {
    // Prevent iOS zoom on input focus
    ...(preventZoom && { fontSize: 'max(16px, 1rem)' }),
    // Better mobile touch targets
    minHeight: '44px',
    // Force text alignment to center (override browser defaults)
    textAlign: 'center' as const,
    ...style
  }

  return (
    <SmartFormField>
      <div className="space-y-3">
        {label && (
          <label
            className="block font-medium text-gray-700 text-center text-fluid-base"
          >
            {label}
          </label>
        )}

        <div className="relative">
          <input
            ref={inputRef}
            {...props}
            className={inputClassName}
            style={inputStyle}
            onKeyDown={handleKeyDown}
            onFocus={handleFocus}
            // iOS-specific optimizations
            autoCapitalize="off"
            autoCorrect="off"
            autoComplete="off"
            enterKeyHint="next"
          />
        </div>

        {helperText && (
          <p
            className="text-center text-gray-500 text-fluid-sm"
          >
            {helperText}
          </p>
        )}
      </div>
    </SmartFormField>
  )
}

/**
 * Enhanced Select Component with Mobile Optimizations
 */
interface MobileOptimizedSelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  label?: string
  onEnterPress?: () => void
}

export function MobileOptimizedSelect({
  label,
  onEnterPress,
  onKeyDown,
  className = '',
  style = {},
  children,
  ...props
}: MobileOptimizedSelectProps) {
  const selectRef = useRef<HTMLSelectElement>(null)

  const handleKeyDown = (e: React.KeyboardEvent<HTMLSelectElement>) => {
    if (e.key === 'Enter' && onEnterPress && !props.disabled) {
      e.preventDefault()
      onEnterPress()
    }

    if (onKeyDown) {
      onKeyDown(e)
    }
  }

  const selectClassName = `w-full bg-white rounded-xl border-2 border-gray-200 font-semibold text-center text-gray-800 focus:outline-none focus:border-brand-blue focus:ring-4 focus:ring-blue-100 transition-all duration-200 shadow-sm ${className}`

  const selectStyle = {
    // Better mobile touch targets
    minHeight: '44px',
    fontSize: 'max(16px, 1rem)', // Prevent iOS zoom
    // Force text alignment to center (override browser defaults)
    textAlign: 'center' as const,
    // Additional centering properties for cross-browser compatibility
    textAlignLast: 'center' as const,
    paddingLeft: '12px',
    // Safari-specific fixes for select element centering
    WebkitAppearance: 'none' as const,
    appearance: 'none' as const,
    backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6,9 12,15 18,9'%3e%3c/polyline%3e%3c/svg%3e")`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 12px center',
    backgroundSize: '16px',
    paddingRight: '40px', // Make room for custom arrow
    ...style
  }

  return (
    <SmartFormField>
      <div className="space-y-3">
        {label && (
          <label
            className="block font-medium text-gray-700 text-center text-fluid-base"
          >
            {label}
          </label>
        )}

        <div className="relative">
          <select
            ref={selectRef}
            {...props}
            className={selectClassName}
            style={selectStyle}
            onKeyDown={handleKeyDown}
            // @ts-ignore - enterKeyHint is valid for select elements
            enterKeyHint="next"
          >
            {children}
          </select>
        </div>
      </div>
    </SmartFormField>
  )
}

/**
 * Button Group Component for Mobile Forms
 * Handles multiple buttons with proper spacing and keyboard awareness
 */
interface MobileButtonGroupProps {
  children: React.ReactNode
  className?: string
  gap?: number
}

export function MobileButtonGroup({
  children,
  className = '',
  gap = 4
}: MobileButtonGroupProps) {
  return (
    <SmartFormField>
      <div
        className={`flex ${className}`}
        style={{ gap: `${gap * 4}px` }} // Convert to pixels
      >
        {children}
      </div>
    </SmartFormField>
  )
}
